import { EngagementSurveyOverviewPageDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { ActivityCatalog } from 'modules/core/lib/activities/catalog'
import { useActivities } from 'modules/core/lib/activities/provider'

const ACTIVITY_IDS = {
  publishSurvey: 'engagement_surveys.publishSurvey' as const,
  finishSurvey: 'engagement_surveys.finishSurvey' as const,
  surveySettings: 'engagement_surveys.surveySettings' as const,
  duplicateSurvey: 'engagement_surveys.duplicateSurvey' as const,
  archiveSurvey: 'engagement_surveys.archiveSurvey' as const,
  deleteSurvey: 'engagement_surveys.deleteSurvey' as const,
  scheduleSettings: 'engagement_surveys.scheduleSettings' as const,
} satisfies Record<string, ActivityCatalog>

export function useSurveyActions() {
  const { surveyId } = useStrictParams<'surveyId'>()
  const { openActivity } = useActivities()
  const { data } = useQuery(EngagementSurveyOverviewPageDocument, {
    variables: { surveyId: Number(surveyId) },
  })
  const survey = data?.surveys.survey

  const actionKeyToActivity: Record<
    string,
    { activity: ActivityCatalog; data: { surveyId?: string; formId?: string } } | undefined
  > = {
    publish: {
      activity: ACTIVITY_IDS.publishSurvey,
      data: { surveyId: surveyId.toString() },
    },
    'change-schedule': {
      activity: ACTIVITY_IDS.publishSurvey,
      data: { surveyId: surveyId.toString() },
    },
    'finish-survey': {
      activity: ACTIVITY_IDS.finishSurvey,
      data: { formId: survey?.form.id.toString() || '' },
    },
    'survey-settings': {
      activity: ACTIVITY_IDS.surveySettings,
      data: { surveyId: surveyId.toString() },
    },
    duplicate: {
      activity: ACTIVITY_IDS.duplicateSurvey,
      data: { surveyId: surveyId.toString() },
    },
    archive: {
      activity: ACTIVITY_IDS.archiveSurvey,
      data: { surveyId: surveyId.toString() },
    },
    delete: {
      activity: ACTIVITY_IDS.deleteSurvey,
      data: { surveyId: surveyId.toString() },
    },
    'edit-publish-date': {
      activity: ACTIVITY_IDS.scheduleSettings,
      data: { formId: survey?.form.id.toString() || '' },
    },
    'edit-recurrence': {
      activity: ACTIVITY_IDS.scheduleSettings,
      data: { formId: survey?.form.id.toString() || '' },
    },
    'edit-ends-at': {
      activity: ACTIVITY_IDS.scheduleSettings,
      data: { formId: survey?.form.id.toString() || '' },
    },
    'edit-days-to-answer': {
      activity: ACTIVITY_IDS.scheduleSettings,
      data: { formId: survey?.form.id.toString() || '' },
    },
  }

  const handleActionClick = (key: string) => {
    if (!survey) return

    const activityConfig = actionKeyToActivity[key]
    if (activityConfig) {
      openActivity(activityConfig.activity, activityConfig.data)
    }
  }

  return {
    handleActionClick,
    survey,
  }
}
